'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Locale } from '@headless-workspace/config';
import { useTailwindBreakpoint } from '@headless-workspace/core-ui';
import { ProductTile } from '@headless-workspace/glow-ds';
import { StringHelpers } from '@headless-workspace/utils';
import { TileProductValue } from '../../../../src';
import { ProductType } from '../types';

type UseProductTilesMapperParamsBase = {
    products: TileProductValue[];
    locale: Locale;
    showStockStatusBtn: boolean;
    enablePriceExceptionLegalMentions: boolean;
};

type UseProductTilesMapperParamsWithMapper<T> = UseProductTilesMapperParamsBase & {
    customMapper: (originalProduct: TileProductValue) => T;
};

type UseProductTilesMapperParamsWithoutMapper = UseProductTilesMapperParamsBase & {
    customMapper?: undefined;
};

type ExtendedProductTile<T = never> = [T] extends [never] ? ProductTile : T & ProductTile;

// Overload when customMapper is provided
export function useProductTilesMapper<T>(params: UseProductTilesMapperParamsWithMapper<T>): ExtendedProductTile<T>[];

// Overload when customMapper is not provided
export function useProductTilesMapper(params: UseProductTilesMapperParamsWithoutMapper): ExtendedProductTile[];

// Implementation
export function useProductTilesMapper<T>({
    products,
    locale,
    showStockStatusBtn,
    enablePriceExceptionLegalMentions,
    customMapper,
}: UseProductTilesMapperParamsWithMapper<T> | UseProductTilesMapperParamsWithoutMapper):
    | ExtendedProductTile<T>
    | ExtendedProductTile[] {
    // TODO : This part should be refactored to pass directly the base property in parameter to allow the customization for each component / page
    const t = useTranslations('Discover.Home.carousel');
    const isMobile = useTailwindBreakpoint('tablet', true, true);

    const getReview = useCallback(
        (reviewsCount?: number) => {
            if (!reviewsCount || reviewsCount === 0) {
                return undefined;
            }
            return { count: reviewsCount, label: t('label.reviews') };
        },
        [t],
    );

    const getVariantPossibilities = useCallback(
        (productType: ProductType, variantsCount?: number) => {
            switch (productType) {
                case ProductType.MONO_SKU:
                    return undefined;
                case ProductType.PRODUCT_SET:
                    return t('label.productSet', { variants: variantsCount });
                case ProductType.MULTI_SKU_SIZE:
                    return t('label.multiFormats', { variants: variantsCount });
                default:
                    return t('label.multiSeveralShades', { variants: variantsCount });
            }
        },
        [t],
    );

    const getStatusLabel = useCallback(
        (isAvailable?: boolean, showStockStatusBtn = false): string => {
            if (!showStockStatusBtn) {
                return t('action.discover');
            }

            if (isAvailable) {
                return isMobile ? t('action.inStockMobile') : t('action.inStock');
            }
            return t('action.outOfStock');
        },
        [isMobile, t],
    );

    const mapProductTile = useCallback(
        (originalProduct: TileProductValue): ExtendedProductTile<T> | ExtendedProductTile => {
            const baseTile: ProductTile = {
                id: originalProduct.id,
                sku: originalProduct.sku,
                brand: originalProduct.brand,
                name: originalProduct.name,
                variantName: originalProduct.variantName,
                description: originalProduct.description,
                href: originalProduct.href,
                image: { ...originalProduct.image, alt: t('ariaLabel.discoverProduct') },

                price: StringHelpers.formatPrice({
                    price: originalProduct.price,
                    currency: originalProduct.currency,
                    locale,
                    defaultValue: null,
                }),
                priceBeforeDiscount: StringHelpers.formatPrice({
                    price: originalProduct.priceBeforeDiscount,
                    currency: originalProduct.currency,
                    locale,
                    defaultValue: null,
                }),
                discountPercentageLabel:
                    originalProduct.discountPercentage && originalProduct.enableDiscountInfo
                        ? t('label.discountPercentage', { discountPercentage: originalProduct.discountPercentage })
                        : undefined,
                pricePerUnit: originalProduct.pricePerUnit,
                minimalProductPriceLabel: t('label.fromPrice'),
                minPrice: StringHelpers.formatPrice({
                    price: originalProduct.minPrice,
                    currency: originalProduct.currency,
                    locale,
                    defaultValue: null,
                }),
                basePriceLabel: originalProduct.originalPriceText,
                totalIndividualPriceLabel: originalProduct.totalIndividualPrice
                    ? t('label.totalIndividualPrice', {
                          value: StringHelpers.formatPrice({
                              price: originalProduct.totalIndividualPrice,
                              currency: originalProduct.currency,
                              locale,
                              defaultValue: null,
                          }),
                      })
                    : undefined,

                isArialFont: originalProduct.isArialFont,
                enableDiscountInfo: originalProduct.enableDiscountInfo,
                priceAlwaysBlack: originalProduct.priceAlwaysBlack,
                isUnderlinedBrand: originalProduct.isUnderlinedBrand,

                type: originalProduct.type,

                tags: originalProduct.tags,

                rating: originalProduct.rating,
                review: getReview(originalProduct.reviewsCount),
                noReviewsLabel: t('label.noReviews'),

                variantPossibilities: getVariantPossibilities(originalProduct.type, originalProduct.variantsCount),
                engravingOptionLabel: originalProduct.isEngravable ? t('label.engravingOption') : undefined,
                legalMentionsLabel: enablePriceExceptionLegalMentions ? t('label.deliveryInformation') : undefined,

                button: {
                    label: getStatusLabel(originalProduct.isAvailable, showStockStatusBtn),
                },
                dataLayer: originalProduct.dataLayer,
            };
            return customMapper ? { ...baseTile, ...customMapper(originalProduct) } : baseTile;
        },
        [
            t,
            locale,
            getReview,
            getVariantPossibilities,
            enablePriceExceptionLegalMentions,
            getStatusLabel,
            showStockStatusBtn,
            customMapper,
        ],
    );

    return useMemo<(ExtendedProductTile<T> | ExtendedProductTile)[]>(() => {
        return products.map(mapProductTile);
    }, [products, mapProductTile]);
}
